// RoomWindow: the framed screenshot that stands in for the old .shot figure.
// Mounted by MeetLatently.tsx, inside .doorway (the room — see
// MeetLatently.css). The room draws the frame (dashed on all four sides),
// so this component only owns the browser-chrome bar and the drift below.
// It paints no vignette over the screenshot: the owner read the old inset
// shadow as a mask, so the image sits flat and clean in its frame.
//
// Depth: while the frame is on screen, the screenshot drifts slightly
// slower than the frame around it (translateY driven by scroll progress),
// so the image reads as sitting behind glass rather than pasted on top.
// The image is pre-scaled 1.06 and .room-window__frame clips overflow, so
// the drift never reveals an edge. Gated by IntersectionObserver (no work
// off-screen) and skipped outright for reduced motion or narrow viewports,
// in which case the transform simply stays at its CSS default (no drift).
import { useEffect, useRef } from 'react';
import './RoomWindow.css';

const PARALLAX_RANGE_PX = 28;

// The image is scaled 1.06 in CSS (.room-window__img), which hides exactly
// (1.06 - 1) / 2 = 3% of the frame's height as overflow on each of the top
// and bottom edges for translateY to move into. At most real column widths
// for this component (900-2200px viewport, per the .flagship__grid layout
// in MeetLatently.css) 3% of the frame's height is well under the full
// +/-14px this constant implies -- e.g. ~10px of headroom at a 1440px
// viewport -- which would push the image past its own hidden overflow and
// expose the frame's background at the top or bottom edge, breaking the
// "no edge ever shows" contract the scale + overflow: hidden pairing exists
// for. Clamped against the frame's own measured height so the drift can
// never exceed the room the 1.06 scale actually bought it.
const IMG_SCALE_OVERFLOW_FRACTION = 0.03;

export function RoomWindow({
  src,
  srcMobile,
  alt,
}: {
  src: string;
  srcMobile: string;
  alt: string;
}): JSX.Element {
  const frameRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const frame = frameRef.current;
    const img = imgRef.current;
    if (!frame || !img) return;

    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    if (reducedMotion || window.innerWidth < 900) return;

    const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

    let rafId: number | null = null;
    let listening = false;

    const applyParallax = () => {
      rafId = null;
      const rect = frame.getBoundingClientRect();
      const vh = window.innerHeight;
      const progress = clamp01((vh - rect.top) / (vh + rect.height));
      const maxDrift = Math.min(
        PARALLAX_RANGE_PX,
        rect.height * IMG_SCALE_OVERFLOW_FRACTION
      );
      // (progress - 0.5), not (0.5 - progress): the frame's own on-screen
      // motion as it scrolls is also negative-going (rect.top decreases),
      // so a NEGATIVE-going translateY here would add to that and make the
      // image's net screen speed exceed the frame's own -- a foreground
      // sticker lifting off the page, the opposite of "behind glass".
      // (progress - 0.5) makes translateY POSITIVE-going instead, so it
      // partially cancels the frame's motion and the image's net screen
      // speed comes out slightly under the frame's -- verified empirically
      // (two-sample scroll delta comparison) before flipping this sign.
      const translateY = (progress - 0.5) * maxDrift;
      img.style.setProperty('--room-parallax', `${translateY}px`);
    };

    const onScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(applyParallax);
    };

    // Only listen to scroll while the frame is actually in view, so the
    // effect does no work for windows that never scroll it into frame.
    const observer = new IntersectionObserver(
      ([entry]) => {
        const isIntersecting = entry?.isIntersecting ?? false;
        if (isIntersecting && !listening) {
          listening = true;
          window.addEventListener('scroll', onScroll, { passive: true });
          applyParallax();
        } else if (!isIntersecting && listening) {
          listening = false;
          window.removeEventListener('scroll', onScroll);
          if (rafId !== null) {
            cancelAnimationFrame(rafId);
            rafId = null;
          }
        }
      },
      { threshold: 0 }
    );
    observer.observe(frame);

    return () => {
      observer.disconnect();
      if (listening) window.removeEventListener('scroll', onScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <figure className="room-window">
      <span className="room-window__bar" aria-hidden="true">
        latently.app
      </span>
      <div className="room-window__frame" ref={frameRef}>
        <picture>
          <source media="(max-width: 639px)" srcSet={srcMobile} />
          <img
            ref={imgRef}
            src={src}
            alt={alt}
            width={1200}
            height={750}
            loading="lazy"
            className="room-window__img"
          />
        </picture>
      </div>
    </figure>
  );
}
